import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  useCallback,
} from "react";
import WindowHeader from "../Layout/WindowHeader/WindowHeader";
import { FiSave, FiLogOut } from "react-icons/fi";
import { useDbContext } from "../../context/DbContext";
import axios from "axios";

const customFormDesignLogo =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><rect x='3' y='3' width='18' height='18' rx='2'/><path d='M9 3v18'/><path d='M3 9h18'/></svg>";

const FormDesign = ({
  onClose,
  onSave,
  formName,
  initialFields = [],
  zIndex,
  highlightFieldId,
}) => {
  const modalZIndex = (zIndex ?? 9999) + 10;
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Read breakout/rowbreak defaults from embedded pseudo-fields in initialFields
  const _defBkCol =
    initialFields.find((f) => f.fieldId === "__GridBreakoutColumn")
      ?.defaultValue || "ItemName";
  const _defBkTgt =
    initialFields.find((f) => f.fieldId === "__GridBreakoutTarget")
      ?.defaultValue || "OtherCharges";
  const _defRbCol =
    initialFields.find((f) => f.fieldId === "__GridRowBreakColumn")
      ?.defaultValue || "Mc";
  const _defRbTgt =
    initialFields.find((f) => f.fieldId === "__GridRowBreakTarget")
      ?.defaultValue || "ItemCode";

  // Breakout Configuration States
  const [breakoutColumn, setBreakoutColumn] = useState(_defBkCol);
  const [breakoutTarget, setBreakoutTarget] = useState(_defBkTgt);

  // Row-Break Configuration States
  const [rowBreakColumn, setRowBreakColumn] = useState(_defRbCol);
  const [rowBreakTarget, setRowBreakTarget] = useState(_defRbTgt);

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (highlightFieldId && !loading) {
      const timer = setTimeout(() => {
        const highlightedEl = tableContainerRef.current?.querySelector(
          '[data-highlighted="true"]',
        );
        if (highlightedEl) {
          highlightedEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [highlightFieldId, loading]);

  // Drag and Drop States
  const [draggedIdx, setDraggedIdx] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === index) return;

    // Live swap items in state
    const reorderedFields = [...fields];
    const [draggedItem] = reorderedFields.splice(draggedIdx, 1);
    reorderedFields.splice(index, 0, draggedItem);

    setFields(reorderedFields);
    setDraggedIdx(index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    // Re-index all tabIndex values sequentially (from 0 to N-1)
    const finalFields = fields.map((field, idx) => ({
      ...field,
      tabIndex: idx,
    }));
    setFields(finalFields);
    setDraggedIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  // Set default dummy fields if initialFields is empty
  const defaultFields =
    initialFields.length > 0
      ? initialFields.map((fld) => ({
          ...fld,
          type: fld.type || "Text",
          font: fld.font || "Inter",
          fontStyle: fld.fontStyle || "Normal",
          fontSize: fld.fontSize || 13,
          width: fld.width || 120,
          height: fld.height || 30,
        }))
      : [
          {
            fieldId: "EntryNo",
            tabIndex: 1,
            defaultValue: "",
            visible: true,
            enabled: true,
            checked: false,
            font: "Inter",
            fontStyle: "Normal",
            fontSize: 13,
            type: "Number",
            width: 120,
            height: 30,
          },
          {
            fieldId: "EntryDate",
            tabIndex: 2,
            defaultValue: "",
            visible: true,
            enabled: true,
            checked: false,
            font: "Inter",
            fontStyle: "Normal",
            fontSize: 13,
            type: "Date",
            width: 150,
            height: 30,
          },
          {
            fieldId: "InvNo",
            tabIndex: 3,
            defaultValue: "",
            visible: true,
            enabled: true,
            checked: false,
            font: "Inter",
            fontStyle: "Normal",
            fontSize: 13,
            type: "Text",
            width: 120,
            height: 30,
          },
          {
            fieldId: "InvDate",
            tabIndex: 4,
            defaultValue: "",
            visible: true,
            enabled: true,
            checked: false,
            font: "Inter",
            fontStyle: "Normal",
            fontSize: 13,
            type: "Date",
            width: 150,
            height: 30,
          },
          {
            fieldId: "Supplier",
            tabIndex: 5,
            defaultValue: "",
            visible: true,
            enabled: true,
            checked: false,
            font: "Inter",
            fontStyle: "Normal",
            fontSize: 13,
            type: "Select",
            width: 200,
            height: 30,
          },
          {
            fieldId: "Location",
            tabIndex: 6,
            defaultValue: "",
            visible: true,
            enabled: true,
            checked: false,
            font: "Inter",
            fontStyle: "Normal",
            fontSize: 13,
            type: "Select",
            width: 200,
            height: 30,
          },
        ];

  useEffect(() => {
    const fetchFormDesign = async () => {
      if (!dbCode || !formName) {
        // Sort and load default fields if no db code
        const sortedInit = [...defaultFields].sort(
          (a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0),
        );
        // Read breakout defaults from embedded pseudo-fields
        const defBkCol = sortedInit.find(
          (f) => f.fieldId === "__GridBreakoutColumn",
        );
        const defBkTgt = sortedInit.find(
          (f) => f.fieldId === "__GridBreakoutTarget",
        );
        const defRbCol = sortedInit.find(
          (f) => f.fieldId === "__GridRowBreakColumn",
        );
        const defRbTgt = sortedInit.find(
          (f) => f.fieldId === "__GridRowBreakTarget",
        );
        if (defBkCol?.defaultValue) setBreakoutColumn(defBkCol.defaultValue);
        if (defBkTgt?.defaultValue) setBreakoutTarget(defBkTgt.defaultValue);
        if (defRbCol?.defaultValue) setRowBreakColumn(defRbCol.defaultValue);
        if (defRbTgt?.defaultValue) setRowBreakTarget(defRbTgt.defaultValue);
        const filtered = sortedInit.filter(
          (f) =>
            !f.fieldId.startsWith("__GridBreakout") &&
            !f.fieldId.startsWith("__GridRowBreak"),
        );
        setFields(filtered);
        setLoading(false);
        return;
      }

      // Check Local Storage first
      let cached = localStorage.getItem(`form_design_${formName}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);

          // Build valid grid column ids from THIS form's defaultFields
          const validGridIds = defaultFields
            .filter((f) => f.fieldId.startsWith("grid_"))
            .map((f) => f.fieldId.replace("grid_", ""));
          // Build valid non-grid field ids (targets for breakout)
          const validTargetIds = defaultFields
            .filter(
              (f) =>
                !f.fieldId.startsWith("grid_") &&
                f.fieldId !== "Grid" &&
                !f.fieldId.startsWith("__"),
            )
            .map((f) => f.fieldId);

          // Load breakout configs — validate against THIS form's fields
          const colMatch = parsed.find(
            (f) => f.fieldId === "__GridBreakoutColumn",
          );
          const tgtMatch = parsed.find(
            (f) => f.fieldId === "__GridBreakoutTarget",
          );
          const cachedBreakoutCol = colMatch?.defaultValue || "";
          const cachedBreakoutTgt = tgtMatch?.defaultValue || "";
          setBreakoutColumn(
            cachedBreakoutCol && validGridIds.includes(cachedBreakoutCol)
              ? cachedBreakoutCol
              : _defBkCol,
          );
          setBreakoutTarget(
            cachedBreakoutTgt && validTargetIds.includes(cachedBreakoutTgt)
              ? cachedBreakoutTgt
              : _defBkTgt,
          );

          // Load row-break configs — validate against THIS form's fields
          const rowBreakColMatch = parsed.find(
            (f) => f.fieldId === "__GridRowBreakColumn",
          );
          const rowBreakTgtMatch = parsed.find(
            (f) => f.fieldId === "__GridRowBreakTarget",
          );
          const cachedRowBreakCol = rowBreakColMatch?.defaultValue || "";
          const cachedRowBreakTgt = rowBreakTgtMatch?.defaultValue || "";
          setRowBreakColumn(
            cachedRowBreakCol && validGridIds.includes(cachedRowBreakCol)
              ? cachedRowBreakCol
              : _defRbCol,
          );
          setRowBreakTarget(
            cachedRowBreakTgt && validGridIds.includes(cachedRowBreakTgt)
              ? cachedRowBreakTgt
              : _defRbTgt,
          );

          // Merge cached fields with defaultFields
          const merged = defaultFields.map((initField) => {
            const cacheMatch = parsed.find(
              (f) =>
                String(f.fieldId).toLowerCase() ===
                String(initField.fieldId).toLowerCase(),
            );

            if (cacheMatch) {
              return {
                ...initField,
                tabIndex:
                  cacheMatch.tabIndex !== undefined &&
                  cacheMatch.tabIndex !== null
                    ? cacheMatch.tabIndex
                    : initField.tabIndex,
                defaultValue:
                  cacheMatch.defaultValue !== undefined &&
                  cacheMatch.defaultValue !== null &&
                  cacheMatch.defaultValue !== ""
                    ? cacheMatch.defaultValue
                    : initField.defaultValue,
                visible:
                  cacheMatch.visible !== undefined &&
                  cacheMatch.visible !== null
                    ? cacheMatch.visible
                    : initField.visible,
                enabled:
                  cacheMatch.enabled !== undefined &&
                  cacheMatch.enabled !== null
                    ? cacheMatch.enabled
                    : initField.enabled,
                checked:
                  cacheMatch.checked !== undefined &&
                  cacheMatch.checked !== null
                    ? cacheMatch.checked
                    : initField.checked,
                font: cacheMatch.font || initField.font,
                fontStyle: cacheMatch.fontStyle || initField.fontStyle,
                fontSize:
                  cacheMatch.fontSize !== undefined &&
                  cacheMatch.fontSize !== null
                    ? cacheMatch.fontSize
                    : initField.fontSize,
                type: cacheMatch.type || initField.type,
                width:
                  cacheMatch.width !== undefined && cacheMatch.width !== null
                    ? cacheMatch.width
                    : initField.width,
                height:
                  cacheMatch.height !== undefined && cacheMatch.height !== null
                    ? cacheMatch.height
                    : initField.height,
              };
            }
            return initField;
          });

          // Sort by tabIndex
          merged.sort((a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0));
          const filtered = merged.filter(
            (f) =>
              !f.fieldId.startsWith("__GridBreakout") &&
              !f.fieldId.startsWith("__GridRowBreak"),
          );
          setFields(filtered);
          setLoading(false);
          return;
        } catch (e) {
          console.error("Error parsing cached form design:", e);
        }
      }

      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/formdesign/${dbCode}/${formName}`,
        );
        const dbFields = response.data || [];

        if (dbFields.length > 0) {
          // Load breakout configs from database
          const colDbMatch = dbFields.find(
            (f) => f.FieldId === "__GridBreakoutColumn",
          );
          const tgtDbMatch = dbFields.find(
            (f) => f.FieldId === "__GridBreakoutTarget",
          );
          if (colDbMatch) setBreakoutColumn(colDbMatch.DefaultVal || _defBkCol);
          else setBreakoutColumn(_defBkCol);
          if (tgtDbMatch) setBreakoutTarget(tgtDbMatch.DefaultVal || _defBkTgt);
          else setBreakoutTarget(_defBkTgt);

          // Load row-break configs from database
          const rowBreakColDbMatch = dbFields.find(
            (f) => f.FieldId === "__GridRowBreakColumn",
          );
          const rowBreakTgtDbMatch = dbFields.find(
            (f) => f.FieldId === "__GridRowBreakTarget",
          );
          if (rowBreakColDbMatch)
            setRowBreakColumn(rowBreakColDbMatch.DefaultVal || _defRbCol);
          else setRowBreakColumn(_defRbCol);
          if (rowBreakTgtDbMatch)
            setRowBreakTarget(rowBreakTgtDbMatch.DefaultVal || _defRbTgt);
          else setRowBreakTarget(_defRbTgt);

          // Merge db fields with defaultFields
          const merged = defaultFields.map((initField) => {
            const dbMatch = dbFields.find(
              (f) =>
                String(f.FieldId).toLowerCase() ===
                String(initField.fieldId).toLowerCase(),
            );

            if (dbMatch) {
              return {
                ...initField,
                tabIndex:
                  dbMatch.TabIndex !== null
                    ? dbMatch.TabIndex
                    : initField.tabIndex,
                defaultValue:
                  dbMatch.DefaultVal !== null &&
                  dbMatch.DefaultVal !== undefined &&
                  dbMatch.DefaultVal !== ""
                    ? dbMatch.DefaultVal
                    : initField.defaultValue,
                visible: dbMatch.Vis === 1,
                enabled: dbMatch.Enb === 1,
                checked: dbMatch.Checked === 1,
                font:
                  dbMatch.FontName !== null ? dbMatch.FontName : initField.font,
                fontStyle:
                  dbMatch.FontStyle !== null
                    ? dbMatch.FontStyle
                    : initField.fontStyle,
                fontSize:
                  dbMatch.FontSize !== null
                    ? dbMatch.FontSize
                    : initField.fontSize,
                type:
                  dbMatch.Type !== undefined && dbMatch.Type !== null
                    ? dbMatch.Type
                    : initField.type,
                width:
                  dbMatch.Width !== undefined && dbMatch.Width !== null
                    ? dbMatch.Width
                    : initField.width,
                height:
                  dbMatch.Height !== undefined && dbMatch.Height !== null
                    ? dbMatch.Height
                    : initField.height,
              };
            }
            return initField;
          });

          // Sort by tabIndex
          merged.sort((a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0));

          // Automatically cache in local storage including breakout and row-break configurations
          const allFieldsToSave = [
            ...merged,
            {
              fieldId: "__GridBreakoutColumn",
              tabIndex: 9999,
              defaultValue: colDbMatch?.DefaultVal || "",
              visible: false,
              enabled: false,
            },
            {
              fieldId: "__GridBreakoutTarget",
              tabIndex: 9999,
              defaultValue: tgtDbMatch?.DefaultVal || "",
              visible: false,
              enabled: false,
            },
            {
              fieldId: "__GridRowBreakColumn",
              tabIndex: 9999,
              defaultValue: rowBreakColDbMatch?.DefaultVal || "",
              visible: false,
              enabled: false,
            },
            {
              fieldId: "__GridRowBreakTarget",
              tabIndex: 9999,
              defaultValue: rowBreakTgtDbMatch?.DefaultVal || "",
              visible: false,
              enabled: false,
            },
          ];
          localStorage.setItem(
            `form_design_${formName}`,
            JSON.stringify(allFieldsToSave),
          );
          const filtered = merged.filter(
            (f) =>
              !f.fieldId.startsWith("__GridBreakout") &&
              !f.fieldId.startsWith("__GridRowBreak"),
          );
          setFields(filtered);
        } else {
          const sortedInit = [...defaultFields].sort(
            (a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0),
          );
          // Read breakout defaults from embedded pseudo-fields
          const defBkCol = sortedInit.find(
            (f) => f.fieldId === "__GridBreakoutColumn",
          );
          const defBkTgt = sortedInit.find(
            (f) => f.fieldId === "__GridBreakoutTarget",
          );
          const defRbCol = sortedInit.find(
            (f) => f.fieldId === "__GridRowBreakColumn",
          );
          const defRbTgt = sortedInit.find(
            (f) => f.fieldId === "__GridRowBreakTarget",
          );
          setBreakoutColumn(defBkCol?.defaultValue || _defBkCol);
          setBreakoutTarget(defBkTgt?.defaultValue || _defBkTgt);
          setRowBreakColumn(defRbCol?.defaultValue || _defRbCol);
          setRowBreakTarget(defRbTgt?.defaultValue || _defRbTgt);
          const filtered = sortedInit.filter(
            (f) =>
              !f.fieldId.startsWith("__GridBreakout") &&
              !f.fieldId.startsWith("__GridRowBreak"),
          );
          setFields(filtered);
        }
      } catch (error) {
        console.error("Error fetching form design:", error);
        const sortedInit = [...defaultFields].sort(
          (a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0),
        );
        // Read breakout defaults from embedded pseudo-fields
        const defBkCol = sortedInit.find(
          (f) => f.fieldId === "__GridBreakoutColumn",
        );
        const defBkTgt = sortedInit.find(
          (f) => f.fieldId === "__GridBreakoutTarget",
        );
        const defRbCol = sortedInit.find(
          (f) => f.fieldId === "__GridRowBreakColumn",
        );
        const defRbTgt = sortedInit.find(
          (f) => f.fieldId === "__GridRowBreakTarget",
        );
        setBreakoutColumn(defBkCol?.defaultValue || _defBkCol);
        setBreakoutTarget(defBkTgt?.defaultValue || _defBkTgt);
        setRowBreakColumn(defRbCol?.defaultValue || _defRbCol);
        setRowBreakTarget(defRbTgt?.defaultValue || _defRbTgt);
        const filtered = sortedInit.filter(
          (f) =>
            !f.fieldId.startsWith("__GridBreakout") &&
            !f.fieldId.startsWith("__GridRowBreak"),
        );
        setFields(filtered);
      } finally {
        setLoading(false);
      }
    };

    fetchFormDesign();
  }, [dbCode, formName, apiBaseUrl]);

  const handleChange = (index, key, val) => {
    const updated = [...fields];
    updated[index][key] = val;
    setFields(updated);
  };

  const handleCheckboxChange = (index, key) => {
    const updated = [...fields];
    updated[index][key] = !updated[index][key];
    setFields(updated);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    try {
      const cleanFields = fields.filter(
        (f) =>
          !f.fieldId.startsWith("__GridBreakout") &&
          !f.fieldId.startsWith("__GridRowBreak"),
      );
      const sortedFields = [...cleanFields].sort(
        (a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0),
      );

      // Append breakout and row-break settings as pseudo-fields
      const configFields = [
        ...sortedFields,
        {
          fieldId: "__GridBreakoutColumn",
          tabIndex: 9999,
          defaultValue: breakoutColumn,
          visible: false,
          enabled: false,
          type: "Config",
        },
        {
          fieldId: "__GridBreakoutTarget",
          tabIndex: 9999,
          defaultValue: breakoutTarget,
          visible: false,
          enabled: false,
          type: "Config",
        },
        {
          fieldId: "__GridRowBreakColumn",
          tabIndex: 9999,
          defaultValue: rowBreakColumn,
          visible: false,
          enabled: false,
          type: "Config",
        },
        {
          fieldId: "__GridRowBreakTarget",
          tabIndex: 9999,
          defaultValue: rowBreakTarget,
          visible: false,
          enabled: false,
          type: "Config",
        },
      ];

      if (dbCode && formName) {
        await axios.post(
          `${apiBaseUrl}/main/formdesign/${dbCode}/${formName}`,
          configFields,
        );
        // Automatically save to local storage
        localStorage.setItem(
          `form_design_${formName}`,
          JSON.stringify(configFields),
        );
      } else {
        localStorage.setItem(
          `form_design_${formName || "default"}`,
          JSON.stringify(configFields),
        );
      }

      if (onSave) {
        onSave(configFields);
      }

      alert("Form configuration saved successfully!");
    } catch (error) {
      console.error("Error saving form design:", error);
      alert("Failed to save form configuration.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetClick = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset all form designs to default?",
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      // Sort initialFields and split real fields from pseudo-fields
      const sortedInit = [...defaultFields].sort(
        (a, b) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0),
      );
      const realFields = sortedInit.filter(
        (f) =>
          !f.fieldId.startsWith("__GridBreakout") &&
          !f.fieldId.startsWith("__GridRowBreak"),
      );

      // Build reset payload: real fields + canonical pseudo-fields from defaultFields
      const resetConfigFields = [
        ...realFields,
        {
          fieldId: "__GridBreakoutColumn",
          tabIndex: 9999,
          defaultValue: _defBkCol,
          visible: false,
          enabled: false,
          type: "Config",
        },
        {
          fieldId: "__GridBreakoutTarget",
          tabIndex: 9999,
          defaultValue: _defBkTgt,
          visible: false,
          enabled: false,
          type: "Config",
        },
        {
          fieldId: "__GridRowBreakColumn",
          tabIndex: 9999,
          defaultValue: _defRbCol,
          visible: false,
          enabled: false,
          type: "Config",
        },
        {
          fieldId: "__GridRowBreakTarget",
          tabIndex: 9999,
          defaultValue: _defRbTgt,
          visible: false,
          enabled: false,
          type: "Config",
        },
      ];

      if (dbCode && formName) {
        await axios.post(
          `${apiBaseUrl}/main/formdesign/${dbCode}/${formName}`,
          resetConfigFields,
        );
        localStorage.setItem(
          `form_design_${formName}`,
          JSON.stringify(resetConfigFields),
        );
      } else {
        localStorage.setItem(
          `form_design_${formName || "default"}`,
          JSON.stringify(resetConfigFields),
        );
      }

      // Reset local modal state to defaults (from initialFields pseudo-fields)
      setBreakoutColumn(_defBkCol);
      setBreakoutTarget(_defBkTgt);
      setRowBreakColumn(_defRbCol);
      setRowBreakTarget(_defRbTgt);

      // Reset display table to real fields only (same as GridDesign → setColumns(initialColumns))
      setFields(realFields);

      // Notify parent to sync context
      if (onSave) {
        onSave(resetConfigFields);
      }

      alert("Form design reset to default successfully!");
    } catch (error) {
      console.error("Error resetting form design:", error);
      alert("Failed to reset form design.");
    } finally {
      setSaving(false);
    }
  };

  // Helper arrays for breakout configuration
  const gridColumnsList = fields
    .filter((f) => f.fieldId.startsWith("grid_"))
    .map((f) => ({
      id: f.fieldId.replace("grid_", ""),
      name: f.fieldName || f.fieldId.replace("grid_", ""),
    }));

  const targetFieldsList = fields
    .filter(
      (f) =>
        !f.fieldId.startsWith("grid_") &&
        f.fieldId !== "Grid" &&
        !f.fieldId.startsWith("__"),
    )
    .map((f) => ({
      id: f.fieldId,
      name: f.fieldName || f.fieldId,
    }));

  if (loading) {
    return (
      <div
        className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-auto font-Inter"
        style={{ zIndex: modalZIndex }}
      >
        <div className="bg-white p-6 rounded-md shadow-lg text-[13px] text-gray-700 font-medium">
          Loading Form Design...
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-auto font-Inter"
      style={{ zIndex: modalZIndex }}
    >
      <div className="w-[1180px] h-[490px] bg-white border border-[#b8cce0] shadow-2xl rounded-[6px] overflow-hidden flex flex-col">
        <style>{`
          select.no-global-focus {
            background-color: white !important;
            background-image: none !important;
            background: white !important;
          }
          select.no-global-focus:focus {
            background-color: white !important;
            background-image: none !important;
            background: white !important;
            outline: none !important;
            box-shadow: none !important;
          }
          select.no-global-focus.focus-blue:focus {
            border-color: #1a3abf !important;
          }
          select.no-global-focus.focus-pink:focus {
            border-color: #b82d60 !important;
          }
        `}</style>
        <WindowHeader
          title={`Form Design - ${formName || ""}`}
          logo={customFormDesignLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        <div className="px-4 py-1 flex-1 flex flex-col gap-2 overflow-hidden">
          {/* Table Container */}
          <div className="flex-1 min-h-0 border border-[#b8cce0] rounded-[4px] overflow-hidden flex flex-col">
            <div ref={tableContainerRef} className="flex-1 overflow-y-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#1a3abf] text-white">
                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[50px] select-none">
                      SINo
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-left border-r border-white/20 select-none">
                      Field ID
                    </th>

                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[80px] select-none">
                      Tab Index
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-left border-r border-white/20 w-[130px] select-none">
                      Default Value
                    </th>
                    <th className="py-1.5 px-2 font-semibold text-center border-r border-white/20 w-[65px] select-none">
                      Visible
                    </th>
                    <th className="py-1.5 px-2 font-semibold text-center border-r border-white/20 w-[65px] select-none">
                      Enabled
                    </th>
                    <th className="py-1.5 px-2 font-semibold text-center border-r border-white/20 w-[65px] select-none">
                      Checked
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[100px] select-none">
                      Type
                    </th>
                    {/* <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[80px] select-none">
                      Width
                    </th> */}
                    {/* <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[80px] select-none">
                      Height
                    </th> */}
                    <th className="py-1.5 px-3 font-semibold text-center border-r border-white/20 w-[80px] select-none">
                      Font Size
                    </th>
                    <th className="py-1.5 px-3 font-semibold text-center w-[100px] select-none">
                      Font Style
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#d6e4f3]">
                  {fields.map((field, idx) => {
                    const isHighlighted =
                      highlightFieldId &&
                      (field.fieldId === highlightFieldId ||
                        (highlightFieldId.startsWith("grid_") &&
                          field.fieldId === "Grid"));
                    return (
                      <tr
                        key={field.fieldId || idx}
                        data-highlighted={isHighlighted ? "true" : "false"}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                        className={`${
                          isHighlighted
                            ? "bg-[#eff6ff]"
                            : idx % 2 === 0
                              ? "bg-[#f8fafc]"
                              : "bg-white"
                        } hover:bg-blue-50/70 transition-all duration-200 cursor-move ${
                          draggedIdx === idx ? "opacity-85 bg-[#dbeafe]" : ""
                        }`}
                        style={
                          draggedIdx === idx
                            ? {
                                outline: "2.5px dashed #2563eb",
                                outlineOffset: "-2.5px",
                                position: "relative",
                                zIndex: 2,
                              }
                            : isHighlighted
                              ? {
                                  outline: "2.5px solid #2563eb",
                                  outlineOffset: "-2.5px",
                                  position: "relative",
                                  zIndex: 1,
                                }
                              : undefined
                        }
                      >
                        {/* SINo */}
                        <td className="py-1 px-3 text-center text-gray-700 font-medium border-r border-[#d6e4f3] select-none w-[50px]">
                          {idx + 1}
                        </td>

                        {/* Field ID */}
                        <td className="py-1 px-3 text-left font-medium text-gray-700 border-r border-[#d6e4f3] select-none">
                          {field.fieldId}
                        </td>

                        {/* Tab Index */}
                        <td className="py-1 px-3 text-center border-r border-[#d6e4f3] w-[80px]">
                          <input
                            type="number"
                            className="w-full h-7 text-center text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                            value={field.tabIndex ?? ""}
                            onChange={(e) =>
                              handleChange(
                                idx,
                                "tabIndex",
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </td>

                        {/* Default Value */}
                        <td className="py-1 px-3 text-left border-r border-[#d6e4f3] w-[130px]">
                          <input
                            type="text"
                            className="w-full h-7 px-2.5 text-left text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                            value={field.defaultValue ?? ""}
                            onChange={(e) =>
                              handleChange(idx, "defaultValue", e.target.value)
                            }
                          />
                        </td>

                        {/* Visible */}
                        <td className="py-1 px-2 text-center border-r border-[#d6e4f3] w-[65px]">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded text-[#1a3abf] border-[#cbd5e1] focus:ring-[#1a3abf] cursor-pointer"
                              checked={!!field.visible}
                              onChange={() =>
                                handleCheckboxChange(idx, "visible")
                              }
                            />
                          </div>
                        </td>

                        {/* Enabled */}
                        <td className="py-1 px-2 text-center border-r border-[#d6e4f3] w-[65px]">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded text-[#1a3abf] border-[#cbd5e1] focus:ring-[#1a3abf] cursor-pointer"
                              checked={!!field.enabled}
                              onChange={() =>
                                handleCheckboxChange(idx, "enabled")
                              }
                            />
                          </div>
                        </td>

                        {/* Checked */}
                        <td className="py-1 px-2 text-center border-r border-[#d6e4f3] w-[65px]">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded text-[#1a3abf] border-[#cbd5e1] focus:ring-[#1a3abf] cursor-pointer"
                              checked={!!field.checked}
                              onChange={() =>
                                handleCheckboxChange(idx, "checked")
                              }
                            />
                          </div>
                        </td>

                        {/* Type */}
                        <td className="py-1 px-3 border-r border-[#d6e4f3] w-[100px]">
                          <select
                            className="w-full h-7 px-1.5 text-left text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter cursor-pointer"
                            value={field.type || "Text"}
                            onChange={(e) =>
                              handleChange(idx, "type", e.target.value)
                            }
                          >
                            <option value="Text">Text</option>
                            <option value="Number">Number</option>
                            <option value="Date">Date</option>
                            <option value="Checkbox">Checkbox</option>
                            <option value="Select">Select</option>
                          </select>
                        </td>

                        {/* Width - commented out for now */}
                        {/* <td className="py-1 px-3 border-r border-[#d6e4f3] w-[80px]">
                        <input
                          type="number"
                          className="w-full h-7 text-center text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                          value={field.width ?? ""}
                          onChange={(e) => handleChange(idx, "width", e.target.value === "" ? "" : Number(e.target.value))}
                        />
                      </td> */}

                        {/* Height - commented out for now */}
                        {/* <td className="py-1 px-3 border-r border-[#d6e4f3] w-[80px]">
                        <input
                          type="number"
                          className="w-full h-7 text-center text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                          value={field.height ?? ""}
                          onChange={(e) => handleChange(idx, "height", e.target.value === "" ? "" : Number(e.target.value))}
                        />
                      </td> */}

                        {/* Font Size */}
                        <td className="py-1 px-3 border-r border-[#d6e4f3] w-[80px]">
                          <input
                            type="number"
                            className="w-full h-7 text-center text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter"
                            value={field.fontSize ?? ""}
                            onChange={(e) =>
                              handleChange(
                                idx,
                                "fontSize",
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </td>

                        {/* Font Style */}
                        <td className="py-1 px-3 w-[100px]">
                          <select
                            className="w-full h-7 px-1.5 text-left text-[13px] border border-[#cbd5e1] rounded-[4px] outline-none focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf] transition-all bg-white box-border font-Inter cursor-pointer"
                            value={field.fontStyle || "Normal"}
                            onChange={(e) =>
                              handleChange(idx, "fontStyle", e.target.value)
                            }
                          >
                            <option value="Normal">Normal</option>
                            <option value="Bold">Bold</option>
                            <option value="Italic">Italic</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Grid Navigation & Breakout Settings */}
          <div className="p-1.5 bg-[#f8fafc] border border-[#e2e8f0] rounded-[6px] text-xs flex-shrink-0 flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-3.5">
              {/* Left Column: Smart Grid Breakout */}
              <div className="flex flex-col gap-1.5 pr-2 border-r border-[#e2e8f0]">
                <div className="font-semibold text-[#1a3abf] flex items-center gap-1 text-xs">
                  <span className="w-1 h-1 rounded-full bg-[#1a3abf]"></span>
                  Smart Grid Breakout
                </div>

                <div className="flex gap-2">
                  <div className="flex-1 flex flex-col gap-0.5">
                    <span className="text-[#475569] font-medium text-[10px]">
                      Trigger Column
                    </span>
                    <select
                      className="no-global-focus focus-blue h-[26px] border border-[#cbd5e1] rounded-[3px] px-1.5 outline-none focus:border-[#1a3abf] bg-white cursor-pointer font-Inter text-[11px]"
                      style={{
                        backgroundImage: "none",
                        backgroundColor: "white",
                        outline: "none",
                        boxShadow: "none",
                      }}
                      value={breakoutColumn}
                      onChange={(e) => setBreakoutColumn(e.target.value)}
                    >
                      <option value="">-- Select Breakpoint Column --</option>
                      {gridColumnsList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 flex flex-col gap-0.5">
                    <span className="text-[#475569] font-medium text-[10px]">
                      Target Field
                    </span>
                    <select
                      className="no-global-focus focus-blue h-[26px] border border-[#cbd5e1] rounded-[3px] px-1.5 outline-none focus:border-[#1a3abf] bg-white cursor-pointer font-Inter text-[11px]"
                      style={{
                        backgroundImage: "none",
                        backgroundColor: "white",
                        outline: "none",
                        boxShadow: "none",
                      }}
                      value={breakoutTarget}
                      onChange={(e) => setBreakoutTarget(e.target.value)}
                    >
                      <option value="">-- Select Target Field --</option>
                      {targetFieldsList.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="text-[10px] text-[#64748b] leading-tight">
                  <strong>Description:</strong> If the user presses{" "}
                  <code>Enter</code> on the selected breakout column while it is
                  empty/null, focus will immediately jump to the target field
                  instead of continuing table entry.
                </div>
              </div>

              {/* Right Column: Smart Row-Break */}
              <div className="flex flex-col gap-1.5">
                <div className="font-semibold text-[#b82d60] flex items-center gap-1 text-xs">
                  <span className="w-1 h-1 rounded-full bg-[#b82d60]"></span>
                  Smart Row-Break
                </div>

                <div className="flex gap-2">
                  <div className="flex-1 flex flex-col gap-0.5">
                    <span className="text-[#475569] font-medium text-[10px]">
                      Trigger Column
                    </span>
                    <select
                      className="no-global-focus focus-pink h-[26px] border border-[#cbd5e1] rounded-[3px] px-1.5 outline-none focus:border-[#b82d60] bg-white cursor-pointer font-Inter text-[11px]"
                      style={{
                        backgroundImage: "none",
                        backgroundColor: "white",
                        outline: "none",
                        boxShadow: "none",
                      }}
                      value={rowBreakColumn}
                      onChange={(e) => setRowBreakColumn(e.target.value)}
                    >
                      <option value="">-- Select Breakpoint Column --</option>
                      {gridColumnsList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 flex flex-col gap-0.5">
                    <span className="text-[#475569] font-medium text-[10px]">
                      Target Column
                    </span>
                    <select
                      className="no-global-focus focus-pink h-[26px] border border-[#cbd5e1] rounded-[3px] px-1.5 outline-none focus:border-[#b82d60] bg-white cursor-pointer font-Inter text-[11px]"
                      style={{
                        backgroundImage: "none",
                        backgroundColor: "white",
                        outline: "none",
                        boxShadow: "none",
                      }}
                      value={rowBreakTarget}
                      onChange={(e) => setRowBreakTarget(e.target.value)}
                    >
                      <option value="">-- Select Target Column --</option>
                      {gridColumnsList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="text-[10px] text-[#64748b] leading-tight">
                  <strong>Description:</strong> When the user presses{" "}
                  <code>Enter</code> on the selected row-break column, the
                  system will save the row and focus the configured target
                  column in the new row.
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 ">
            <button
              onClick={handleResetClick}
              disabled={saving}
              className="px-5 text-[13px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:bg-red-100 rounded-[4px] transition-colors border border-red-200 cursor-pointer flex items-center gap-2"
              style={{ height: "34px" }}
            >
              Reset to Default
            </button>
            <button
              onClick={handleSaveClick}
              disabled={saving}
              className="px-5 text-[13px] font-semibold text-white bg-[#1a3abf] hover:bg-blue-800 disabled:bg-blue-300 rounded-[4px] transition-colors border-none cursor-pointer flex items-center gap-2"
              style={{ height: "34px" }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="text-base" />
                  Save
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-5 text-[13px] font-semibold text-[#334155] bg-[#EAF0FC] hover:bg-blue-100 rounded-[4px] transition-colors border border-[#b8cce0]/50 cursor-pointer flex items-center gap-2"
              style={{ height: "34px" }}
            >
              <FiLogOut className="text-base" />
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormDesign;

// ── Focus Management Context & Provider for Reusable Form Navigation ──
export const FormDesignContext = createContext(null);

export const FormDesignProvider = ({
  children,
  formName,
  defaultFields,
  dbCode,
  apiBaseUrl,
}) => {
  // Read breakout/rowbreak defaults from embedded pseudo-fields in defaultFields
  const _defBkCol =
    defaultFields?.find((f) => f.fieldId === "__GridBreakoutColumn")
      ?.defaultValue || "ItemName";
  const _defBkTgt =
    defaultFields?.find((f) => f.fieldId === "__GridBreakoutTarget")
      ?.defaultValue || "OtherCharges";
  const _defRbCol =
    defaultFields?.find((f) => f.fieldId === "__GridRowBreakColumn")
      ?.defaultValue || "Mc";
  const _defRbTgt =
    defaultFields?.find((f) => f.fieldId === "__GridRowBreakTarget")
      ?.defaultValue || "ItemCode";

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [breakoutColumn, setBreakoutColumn] = useState(_defBkCol);
  const [breakoutTarget, setBreakoutTarget] = useState(_defBkTgt);
  const [rowBreakColumn, setRowBreakColumn] = useState(_defRbCol);
  const [rowBreakTarget, setRowBreakTarget] = useState(_defRbTgt);
  const [activeFieldId, setActiveFieldId] = useState(null);

  const elementsRegistry = useRef({});
  // Registry mapping fieldId → tab-switch callback
  // Used when focus target is in an inactive (unmounted) tab
  const tabSwitcherRegistry = useRef({});

  useEffect(() => {
    const handleFocusIn = (event) => {
      const activeEl = event.target;
      if (!activeEl) return;

      const registry = elementsRegistry.current;
      for (const [fieldId, el] of Object.entries(registry)) {
        if (el === activeEl || el?.contains?.(activeEl)) {
          setActiveFieldId(fieldId);
          return;
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  // Register element ref callback
  const registerRef = useCallback(
    (fieldId) => (el) => {
      if (el) {
        elementsRegistry.current[fieldId] = el;
      } else {
        delete elementsRegistry.current[fieldId];
      }
    },
    [],
  );

  /**
   * registerTabSwitcher(fieldId, switchFn)
   * Call this for every field that lives inside a conditionally-rendered tab panel.
   * switchFn() should make that tab panel visible (e.g. setActiveTab("Payroll")).
   * When focusField is called for a field whose DOM element is not yet mounted,
   * FormDesign will invoke switchFn() and retry focus after React re-renders.
   *
   * Example usage in a component:
   *   useEffect(() => {
   *     registerTabSwitcher("Commission", () => setActiveTab("payroll"));
   *     registerTabSwitcher("Active",     () => setActiveTab("payroll"));
   *   }, [registerTabSwitcher]);
   */
  const registerTabSwitcher = useCallback((fieldId, switchFn) => {
    if (typeof switchFn === "function") {
      tabSwitcherRegistry.current[fieldId] = switchFn;
    } else {
      delete tabSwitcherRegistry.current[fieldId];
    }
  }, []);

  // Synchronize configuration values when fields change
  useEffect(() => {
    if (fields && fields.length > 0) {
      const bkCol =
        fields.find((f) => f.fieldId === "__GridBreakoutColumn")
          ?.defaultValue || _defBkCol;
      const bkTgt =
        fields.find((f) => f.fieldId === "__GridBreakoutTarget")
          ?.defaultValue || _defBkTgt;
      const rbCol =
        fields.find((f) => f.fieldId === "__GridRowBreakColumn")
          ?.defaultValue || _defRbCol;
      const rbTgt =
        fields.find((f) => f.fieldId === "__GridRowBreakTarget")
          ?.defaultValue || _defRbTgt;

      setBreakoutColumn(bkCol);
      setBreakoutTarget(bkTgt);
      setRowBreakColumn(rbCol);
      setRowBreakTarget(rbTgt);
    }
  }, [fields, _defBkCol, _defBkTgt, _defRbCol, _defRbTgt]);

  // Fetch and Merge Form Design Settings
  useEffect(() => {
    const loadFormDesign = async () => {
      if (!dbCode || !formName) {
        const sorted = [...defaultFields].sort(
          (a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999),
        );
        setFields(sorted);
        setLoading(false);
        return;
      }

      // Check local storage cache first
      let cached = localStorage.getItem(`form_design_${formName}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);

          // Load grid breakout pseudo fields configuration
          const colMatch = parsed.find(
            (f) => f.fieldId === "__GridBreakoutColumn",
          );
          const tgtMatch = parsed.find(
            (f) => f.fieldId === "__GridBreakoutTarget",
          );
          if (colMatch) setBreakoutColumn(colMatch.defaultValue || _defBkCol);
          else setBreakoutColumn(_defBkCol);
          if (tgtMatch) setBreakoutTarget(tgtMatch.defaultValue || _defBkTgt);
          else setBreakoutTarget(_defBkTgt);

          // Load row-break configs
          const rowBreakColMatch = parsed.find(
            (f) => f.fieldId === "__GridRowBreakColumn",
          );
          const rowBreakTgtMatch = parsed.find(
            (f) => f.fieldId === "__GridRowBreakTarget",
          );
          if (rowBreakColMatch)
            setRowBreakColumn(rowBreakColMatch.defaultValue || _defRbCol);
          else setRowBreakColumn(_defRbCol);
          if (rowBreakTgtMatch)
            setRowBreakTarget(rowBreakTgtMatch.defaultValue || _defRbTgt);
          else setRowBreakTarget(_defRbTgt);

          const merged = defaultFields.map((defFld) => {
            const match = parsed.find((p) => p.fieldId === defFld.fieldId);
            if (match) {
              return {
                ...defFld,
                ...match,
                defaultValue:
                  match.defaultValue !== undefined &&
                  match.defaultValue !== null &&
                  match.defaultValue !== ""
                    ? match.defaultValue
                    : defFld.defaultValue,
              };
            }
            return defFld;
          });
          merged.sort((a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999));
          setFields(merged);
          setLoading(false);
          return;
        } catch (e) {
          console.error("Error parsing cached form design:", e);
        }
      }

      // Fetch from API
      try {
        const response = await axios.get(
          `${apiBaseUrl}/main/formdesign/${dbCode}/${formName}`,
        );
        const dbFields = response.data || [];

        const colDbMatch = dbFields.find(
          (f) => f.FieldId === "__GridBreakoutColumn",
        );
        const tgtDbMatch = dbFields.find(
          (f) => f.FieldId === "__GridBreakoutTarget",
        );
        if (colDbMatch) setBreakoutColumn(colDbMatch.DefaultVal || _defBkCol);
        else setBreakoutColumn(_defBkCol);
        if (tgtDbMatch) setBreakoutTarget(tgtDbMatch.DefaultVal || _defBkTgt);
        else setBreakoutTarget(_defBkTgt);

        const rowBreakColDbMatch = dbFields.find(
          (f) => f.FieldId === "__GridRowBreakColumn",
        );
        const rowBreakTgtDbMatch = dbFields.find(
          (f) => f.FieldId === "__GridRowBreakTarget",
        );
        if (rowBreakColDbMatch)
          setRowBreakColumn(rowBreakColDbMatch.DefaultVal || _defRbCol);
        else setRowBreakColumn(_defRbCol);
        if (rowBreakTgtDbMatch)
          setRowBreakTarget(rowBreakTgtDbMatch.DefaultVal || _defRbTgt);
        else setRowBreakTarget(_defRbTgt);

        const merged = defaultFields.map((defFld) => {
          const dbMatch = dbFields.find(
            (f) =>
              String(f.FieldId).toLowerCase() ===
              String(defFld.fieldId).toLowerCase(),
          );
          if (dbMatch) {
            return {
              ...defFld,
              tabIndex:
                dbMatch.TabIndex !== null && dbMatch.TabIndex !== undefined
                  ? dbMatch.TabIndex
                  : defFld.tabIndex,
              defaultValue:
                dbMatch.DefaultVal !== null &&
                dbMatch.DefaultVal !== undefined &&
                dbMatch.DefaultVal !== ""
                  ? dbMatch.DefaultVal
                  : defFld.defaultValue,
              visible: dbMatch.Vis === 1,
              enabled: dbMatch.Enb === 1,
              checked: dbMatch.Checked === 1,
              font: dbMatch.FontName || defFld.font,
              fontStyle: dbMatch.FontStyle || defFld.fontStyle,
              fontSize: dbMatch.FontSize || defFld.fontSize,
              type: dbMatch.Type || defFld.type,
              width: dbMatch.Width || defFld.width,
              height: dbMatch.Height || defFld.height,
              required: dbMatch.Required === 1,
              focusable: dbMatch.Focusable !== 0,
            };
          }
          return defFld;
        });

        merged.sort((a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999));

        // Save in local storage
        const allFieldsToSave = [
          ...merged,
          {
            fieldId: "__GridBreakoutColumn",
            tabIndex: 9999,
            defaultValue: colDbMatch?.DefaultVal || "",
            visible: false,
            enabled: false,
          },
          {
            fieldId: "__GridBreakoutTarget",
            tabIndex: 9999,
            defaultValue: tgtDbMatch?.DefaultVal || "",
            visible: false,
            enabled: false,
          },
          {
            fieldId: "__GridRowBreakColumn",
            tabIndex: 9999,
            defaultValue: rowBreakColDbMatch?.DefaultVal || "",
            visible: false,
            enabled: false,
          },
          {
            fieldId: "__GridRowBreakTarget",
            tabIndex: 9999,
            defaultValue: rowBreakTgtDbMatch?.DefaultVal || "",
            visible: false,
            enabled: false,
          },
        ];
        localStorage.setItem(
          `form_design_${formName}`,
          JSON.stringify(allFieldsToSave),
        );
        setFields(merged);
      } catch (error) {
        console.error("Error fetching form design:", error);
        const sorted = [...defaultFields].sort(
          (a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999),
        );
        setBreakoutColumn(_defBkCol);
        setBreakoutTarget(_defBkTgt);
        setRowBreakColumn(_defRbCol);
        setRowBreakTarget(_defRbTgt);
        setFields(sorted);
      } finally {
        setLoading(false);
      }
    };

    loadFormDesign();
  }, [formName, dbCode, apiBaseUrl, defaultFields]);

  // Internal helper that performs the actual DOM focus
  const _doFocus = useCallback((fieldId) => {
    const el = elementsRegistry.current[fieldId];
    if (el) {
      if (typeof el.focus === "function") {
        el.focus();
        if (el.select) el.select();
      } else if (el.current && typeof el.current.focus === "function") {
        el.current.focus();
      } else {
        const inputEl = el.querySelector?.("input, select, textarea, button");
        if (inputEl) {
          inputEl.focus();
          if (inputEl.select) inputEl.select();
        }
      }
    } else {
      const domEl =
        document.getElementById(fieldId) ||
        document.querySelector(`[data-field-id="${fieldId}"]`);
      if (domEl) {
        domEl.focus();
        if (domEl.select) domEl.select();
      }
    }
  }, []);

  /**
   * Imperatively focus a fieldId.
   * If the field's DOM element is not yet mounted (e.g. it lives in an inactive tab),
   * this function will:
   *   1. Call the registered tab-switcher to make the tab visible.
   *   2. Retry the focus after React re-renders (via rAF + setTimeout).
   */
  const focusField = useCallback((fieldId) => {
    const el = elementsRegistry.current[fieldId];
    const domNode = el && el.current ? el.current : el;
    const isNode = domNode && domNode instanceof Node;
    const isInDom = isNode && (document.body.contains(domNode) || domNode.nodeType === Node.ELEMENT_NODE && document.contains(domNode));

    if (isInDom) {
      // Element is already mounted — focus immediately
      _doFocus(fieldId);
    } else {
      // Element is not in DOM — check if there is a tab-switcher registered
      const switchFn = tabSwitcherRegistry.current[fieldId];
      if (switchFn) {
        // Switch the tab to make it visible
        switchFn();
        // Wait for React to re-render the tab content, then focus
        requestAnimationFrame(() => {
          setTimeout(() => {
            _doFocus(fieldId);
          }, 50);
        });
      } else {
        // No tab-switcher — just try focusing directly (fallback)
        _doFocus(fieldId);
      }
    }
  }, [_doFocus]);

  // Initial focus on load
  const triggerInitialFocus = useCallback(() => {
    if (!loading && fields.length > 0) {
      const sortedFocusable = fields
        .filter(
          (f) =>
            f.visible !== false &&
            f.enabled !== false &&
            f.focusable !== false &&
            !f.fieldId.startsWith("__"),
        )
        .sort((a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999));

      if (sortedFocusable.length > 0) {
        const firstField = sortedFocusable[0];
        if (
          firstField.type === "Grid" ||
          firstField.fieldId.toLowerCase() === "grid"
        ) {
          const firstGridField = fields.find(
            (f) =>
              f.fieldId.startsWith("grid_") &&
              f.visible !== false &&
              f.enabled !== false,
          );
          if (firstGridField) {
            focusField(`grid_0_${firstGridField.fieldId.replace("grid_", "")}`);
          }
        } else {
          focusField(firstField.fieldId);
        }
      }
    }
  }, [loading, fields, focusField]);

  // Trigger initial focus once fields are loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      triggerInitialFocus();
    }, 350);
    return () => clearTimeout(timer);
  }, [loading, fields, triggerInitialFocus]);

  // Sequential focus navigation
  const focusNextField = useCallback(
    (currentFieldId) => {
      const sortedFocusable = fields
        .filter(
          (f) =>
            f.visible !== false &&
            f.enabled !== false &&
            f.focusable !== false &&
            !f.fieldId.startsWith("__"),
        )
        .sort((a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999));

      const currentIndex = sortedFocusable.findIndex(
        (f) => f.fieldId === currentFieldId,
      );
      if (currentIndex !== -1 && currentIndex < sortedFocusable.length - 1) {
        const nextField = sortedFocusable[currentIndex + 1];

        // If entering a Grid
        if (
          nextField.type === "Grid" ||
          nextField.fieldId.toLowerCase() === "grid"
        ) {
          const firstGridField = fields.find(
            (f) =>
              f.fieldId.startsWith("grid_") &&
              f.visible !== false &&
              f.enabled !== false,
          );
          if (firstGridField) {
            focusField(`grid_0_${firstGridField.fieldId.replace("grid_", "")}`);
          } else {
            // Skip grid
            const afterGrid = sortedFocusable[currentIndex + 2];
            if (afterGrid) focusField(afterGrid.fieldId);
          }
        } else {
          focusField(nextField.fieldId);
        }
      }
    },
    [fields, focusField],
  );

  // Main KeyDown handler coordinating focus updates
  const handleKeyDown = useCallback(
    (event, currentFieldId) => {
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();

        // Check if this is a grid cell registration: grid_${rowIndex}_${columnName}
        const gridMatch = currentFieldId.match(/^grid_(\d+)_(.+)$/);
        if (gridMatch) {
          const rowIndex = parseInt(gridMatch[1], 10);
          const colName = gridMatch[2];

          // Breakout Logic: check if value is empty and this column is configured as the breakout trigger
          console.log("FormDesign KeyDown gridMatch:", {
            rowIndex,
            colName,
            breakoutColumn,
            breakoutTarget,
            val: event.target.value,
          });
          if (breakoutColumn && colName === breakoutColumn) {
            const val = event.target.value;
            if (!val || val.trim() === "") {
              console.log(
                "FormDesign KeyDown Breakout Triggered! Target:",
                breakoutTarget,
              );
              if (breakoutTarget) {
                focusField(breakoutTarget);
              }
              return;
            }
          }

          // Standard Grid navigation based on formdesign sorted grid tab indexes
          const gridFields = fields
            .filter(
              (f) =>
                f.fieldId.startsWith("grid_") &&
                f.visible !== false &&
                f.enabled !== false,
            )
            .sort((a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999));

          const gridColNames = gridFields.map((f) =>
            f.fieldId.replace("grid_", ""),
          );
          const colIdx = gridColNames.indexOf(colName);

          if (colIdx !== -1) {
            // Find the next column in the same row that has a registered element
            let targetColIdx = colIdx + 1;
            let foundNext = false;
            while (targetColIdx < gridColNames.length) {
              const nextCol = gridColNames[targetColIdx];
              const nextCellId = `grid_${rowIndex}_${nextCol}`;
              if (elementsRegistry.current[nextCellId]) {
                focusField(nextCellId);
                foundNext = true;
                break;
              }
              targetColIdx++;
            }

            if (!foundNext) {
              // Last column: try next row first column
              const nextRowIndex = rowIndex + 1;
              const firstCol = gridColNames[0];
              const nextCellId = `grid_${nextRowIndex}_${firstCol}`;

              if (elementsRegistry.current[nextCellId]) {
                focusField(nextCellId);
              } else {
                // Next row doesn't exist yet, check if there's a breakout target to jump to
                if (breakoutTarget) {
                  focusField(breakoutTarget);
                }
              }
            }
          }
        } else {
          // Normal sequential field focus movement
          focusNextField(currentFieldId);
        }
      }
    },
    [fields, breakoutColumn, breakoutTarget, focusNextField, focusField],
  );

  // Style helper implementations
  const getFieldConfig = useCallback(
    (fieldId) => {
      return fields.find((f) => f.fieldId === fieldId) || {};
    },
    [fields],
  );

  const getLabelStyle = useCallback(
    (fieldId, defaultStyles = {}) => {
      const config = getFieldConfig(fieldId);
      if (!config) return defaultStyles;

      const styleOverride = {};
      if (config.font) styleOverride.fontFamily = config.font;
      if (config.labelFontSize && config.labelFontSize !== 0) {
        styleOverride.fontSize = `${config.labelFontSize}px`;
      } else if (config.fontSize && config.fontSize !== 0) {
        styleOverride.fontSize = `${config.fontSize}px`;
      }
      if (config.fontStyle) {
        const fs = config.fontStyle.toLowerCase();
        if (fs.includes("bold")) styleOverride.fontWeight = "bold";
        if (fs.includes("italic")) styleOverride.fontStyle = "italic";
      }
      if (config.labelColor) styleOverride.color = config.labelColor;

      return { ...defaultStyles, ...styleOverride };
    },
    [getFieldConfig],
  );

  const getInputStyle = useCallback(
    (fieldId, defaultStyles = {}) => {
      const config = getFieldConfig(fieldId);
      if (!config) return defaultStyles;

      const styleOverride = {};
      if (config.font) styleOverride.fontFamily = config.font;
      if (config.fontSize && config.fontSize !== 0)
        styleOverride.fontSize = `${config.fontSize}px`;
      if (config.fontStyle) {
        const fs = config.fontStyle.toLowerCase();
        if (fs.includes("bold")) styleOverride.fontWeight = "bold";
        if (fs.includes("italic")) styleOverride.fontStyle = "italic";
      }
      if (config.inputColor) styleOverride.color = config.inputColor;
      if (config.bgColor) styleOverride.backgroundColor = config.bgColor;
      if (config.borderColor) styleOverride.borderColor = config.borderColor;
      if (config.alignment) styleOverride.textAlign = config.alignment;
      if (config.width && config.width !== 0)
        styleOverride.width = `${config.width}px`;
      if (config.height && config.height !== 0)
        styleOverride.height = `${config.height}px`;

      return { ...defaultStyles, ...styleOverride };
    },
    [getFieldConfig],
  );

  const getContainerStyle = useCallback(
    (fieldId, defaultStyles = {}) => {
      const config = getFieldConfig(fieldId);
      if (!config) return defaultStyles;
      return {
        ...defaultStyles,
        display: config.visible === false ? "none" : undefined,
      };
    },
    [getFieldConfig],
  );

  const isFieldVisible = useCallback(
    (fieldId) => {
      const config = getFieldConfig(fieldId);
      return config.visible !== false;
    },
    [getFieldConfig],
  );

  const isFieldDisabled = useCallback(
    (fieldId) => {
      const config = getFieldConfig(fieldId);
      return config.enabled === false;
    },
    [getFieldConfig],
  );

  const isFieldReadOnly = useCallback(
    (fieldId) => {
      const config = getFieldConfig(fieldId);
      return config.readOnly === true;
    },
    [getFieldConfig],
  );

  const contextValue = {
    fields,
    loading,
    breakoutColumn,
    breakoutTarget,
    rowBreakColumn,
    rowBreakTarget,
    setBreakoutColumn,
    setBreakoutTarget,
    setRowBreakColumn,
    setRowBreakTarget,
    registerRef,
    registerTabSwitcher,
    handleKeyDown,
    focusField,
    getFieldConfig,
    getLabelStyle,
    getInputStyle,
    getContainerStyle,
    isFieldVisible,
    isFieldDisabled,
    isFieldReadOnly,
    triggerInitialFocus,
    setFields,
    activeFieldId,
  };

  return (
    <FormDesignContext.Provider value={contextValue}>
      {children}
    </FormDesignContext.Provider>
  );
};

export const useFormDesign = () => {
  const context = useContext(FormDesignContext);
  if (!context) {
    throw new Error("useFormDesign must be used within a FormDesignProvider");
  }
  return context;
};
